/**
 * web/src/components/workflow/FilterBar.tsx
 * Search + status filters for the Sponsors list (UI-03).
 *
 * Layout (handoff §Workflows §Filter bar; design_files/styles.css `.filter-bar`):
 *   [search input — max 360px, leading icon] [status select] [show-archived toggle] [show-test toggle (admin)]
 *
 * The type select was removed in the 2026-07 nav restructure — the list is
 * sponsor-scoped now (speakers live in the Speakers roster/timeline), so the
 * only non-sponsor rows are admin test workflows, covered by the checkbox.
 *
 * Sort is omitted in V1 — server orders by createdAt desc.
 *
 * Test workflow visibility (CONTEXT.md "Claude's Discretion"):
 *  - The "Show test workflows" checkbox is admin-only.
 *  - All non-admin roles never see test_workflow rows; admin opts in explicitly.
 */
import type { ChangeEvent } from 'react';
import { Input } from '../shared/Input';
import { Select } from '../shared/Select';
import { Icon } from '../../lib/icons';
import { cn } from '../../lib/cn';

/**
 * Status filter buckets. These are NOT raw WorkflowStatus values — a workflow's
 * top-level status is just 'paused' for gate-waiting, rejected, AND awaiting an
 * external reply, so the buckets are derived from the current step's status
 * (see deriveWorkflowStatusKey + matchesStatusFilter in WorkflowList):
 *   active  = running OR any paused (everything in-flight, incl. rejected)
 *   waiting = awaiting approval OR awaiting reply (NOT rejected)
 *   rejected / completed / failed / cancelled = exactly that
 */
export type StatusFilter =
  | 'all'
  | 'active'
  | 'waiting'
  | 'rejected'
  | 'completed'
  | 'failed'
  | 'cancelled';

/** Filter state mirrored to URL query params on the WorkflowList page. */
export interface FilterState {
  search: string;
  status: StatusFilter;
  showTest: boolean;
  /** Include archived (soft-hidden) workflows in the list. Default off. */
  showArchived: boolean;
}

/** Public props for FilterBar. */
export interface FilterBarProps {
  value: FilterState;
  onChange: (next: FilterState) => void;
  isAdmin: boolean;
  className?: string;
}

/**
 * Renders the workflow list filter bar. Controlled — caller owns the
 * FilterState (typically WorkflowList), so URL-sync and other side effects
 * happen there.
 *
 * @param value current filter state
 * @param onChange called with the next FilterState on every input change
 * @param isAdmin gates the test_workflow option + showTest checkbox
 * @sideEffects none — pure controlled component
 */
export function FilterBar({ value, onChange, isAdmin, className }: FilterBarProps) {
  const set = <K extends keyof FilterState>(k: K, v: FilterState[K]) =>
    onChange({ ...value, [k]: v });

  return (
    <div className={cn('flex items-center gap-2.5 mb-6 flex-wrap', className)}>
      <div className="relative flex-1 max-w-[360px] min-w-[220px]">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-subtle pointer-events-none">
          <Icon name="search" size={14} />
        </span>
        <Input
          type="search"
          placeholder="Search by company..."
          value={value.search}
          onChange={(e: ChangeEvent<HTMLInputElement>) => set('search', e.target.value)}
          className="pl-8"
          aria-label="Search workflows by company"
        />
      </div>

      <Select
        aria-label="Status filter"
        value={value.status}
        onChange={(e) => set('status', e.target.value as FilterState['status'])}
        className="min-w-[140px]"
      >
        <option value="all">All statuses</option>
        <option value="active">Active (running + waiting)</option>
        <option value="waiting">Waiting</option>
        <option value="rejected">Rejected</option>
        <option value="completed">Completed</option>
        <option value="failed">Failed</option>
        <option value="cancelled">Cancelled</option>
      </Select>

      {/* Open to everyone — ops needs to find runs they took offline. */}
      <label className="inline-flex items-center gap-2 text-[12px] text-text-muted cursor-pointer ml-2">
        <input
          type="checkbox"
          className="cursor-pointer"
          checked={value.showArchived}
          onChange={(e) => set('showArchived', e.target.checked)}
        />
        Show archived
      </label>

      {isAdmin && (
        <label className="inline-flex items-center gap-2 text-[12px] text-text-muted cursor-pointer ml-2">
          <input
            type="checkbox"
            className="cursor-pointer"
            checked={value.showTest}
            onChange={(e) => set('showTest', e.target.checked)}
          />
          Show test workflows
        </label>
      )}
    </div>
  );
}
